import { useEffect, useState } from "react";

import { TextEditor, Checkbox, Typography, DocumentList, Button, Modal, ArrowButton } from "@king-ict/eupisi-ui/components";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router";

import { getNotification, downloadDocument, deleteNotification } from "../../api/nisooApi";
import StaticField from "../../components/StaticField";
import { DocumentType } from "../../models/Document";
import { Notification } from "../../models/Notification";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetUserRolesQuery } from "../../queries/codeBookQuery"
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { notificationEditUrl, notificationAdministrationRoute } from "../../routes/routes";
import styles from "./NotificationList.module.scss";

export function NotificationDetails(){

    const { t } = useTranslation();
    const history = useHistory();
    const {data: notificationRoles} = useGetUserRolesQuery();
    const { data: userSession } = useGetUserSessionQuery();  
    const params = useParams<{ notificationId: string }>();
    const [notification, setNotification] = useState<Notification>();  
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openSuccessDeleteModal, setOpenSuccessDeleteModal] = useState(false);

    useEffect(() => {

        if(!userSession || (userSession.roleId !== RoleEnum.ASOO && userSession.roleId !== RoleEnum.ASOOAdmin && userSession.roleId !== RoleEnum.ASOOOffice 
            && userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.MZOMAdmin && userSession.roleId !== RoleEnum.MZOMOffice 
            && userSession.roleId !== RoleEnum.Helpdesk)){
            return;
        }

        //dohvat po idju
        const fetchNotification = async () => {
            var result = await getNotification(parseInt(params.notificationId));
            setNotification(result);            
        };

        fetchNotification();

    }, [params.notificationId, userSession]);

    const handleDocumentDownload = async (document: DocumentData) => {
        if(document && document.id)
        {
            await downloadDocument(DocumentType.NotificationAttachment, document.id);
        }
    };

    const onDeleteNotificationClick = () => {
        setOpenDeleteModal(true);
    };

    const onEditNotificationClick = () => {
        if(notification && notification.id){
            history.push(notificationEditUrl(notification?.id));
        }
        
    };

    const handleCancelDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleConfirmDeleteModal = async () => {
        setOpenDeleteModal(false);        

        await deleteNotification(notification!.id).then(() => {
            setOpenSuccessDeleteModal(true);
        })        
    };

    const handleConfirmSuccessDeleteModal = () => {
        setOpenSuccessDeleteModal(false);
        history.push(notificationAdministrationRoute());        
    };

    const onBackToNotificationAdministrationClick = () => {
        history.push(notificationAdministrationRoute());
    };

    //glupi hack radi čudnog postavljanja TextEditora -> dao bi se neki bolji hook vjerojatno koristiti ali nemam vremena za to -> TODO
    if(!notification?.message)
        return null;
    return (
        <>
            <Row>
                <Col md={6}>
                    <ArrowButton
                        backwards
                        ariaLabel={t("common.returnToNotificationAdministration")}
                        onClick={onBackToNotificationAdministrationClick}
                    />
                    <span className={styles.backButtonCaption}>
                        {t("common.returnToNotificationAdministration")}
                    </span>
                </Col>
            </Row>
            <br />
            <Row>
                <Col md={8}>
                    <h2>{t("common.notification")}</h2>
                </Col>
                <Col md={4}>
                    <Button
                        outline
                        onClick={onDeleteNotificationClick}
                        className={styles.actionButton}
                        color="danger"
                    >
                        {t("common.delete")}
                    </Button>
                    <Button
                        outline
                        onClick={onEditNotificationClick}
                        className={styles.actionButton}
                    >
                        {t("common.edit")}
                    </Button>
                </Col>                
            </Row>
            <div className="mt-4 pr-5 mr-5">
                <div className={styles.panel}>
                    <Row>
                        <Col md={12}>
                            <StaticField 
                                label={t("notification.title")} 
                                value={notification?.title} 
                            />                        
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <TextEditor
                                label={t("notification.message")}
                                initialValue={notification?.message}
                                handleChange={() => {}}
                                plaintext
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={5}>
                            <StaticField
                                label={t("notification.validFrom")}
                                value={notification?.validFrom ? new Date(notification?.validFrom).toLocaleString("hr-HR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"                        
                                }) : ""}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Checkbox
                                label={t("notification.putOnTop")}
                                checked={notification?.putOnTop}
                                plaintext
                                />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <StaticField
                                label={t("notification.intendedForRoles")}
                                value={notification?.roles.map(r => notificationRoles?.find(nr => nr.id === r)?.name).join(", ")}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Checkbox
                                label={t("notification.isActive")}
                                checked={notification?.isActive}
                                plaintext
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Typography variant="medium" component="div" color="light">
                                {t("notification.attachments")}
                            </Typography>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <DocumentList
                                documents={notification?.attachments ?? []}
                                removable={false}
                                onDocumentClick={handleDocumentDownload}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
            <Modal
                type="confirm"
                open={openDeleteModal}
                confirmText={t("common.yesDelete")}
                cancelText={t("common.cancel")}
                onClose={handleCancelDeleteModal}
                onConfirm={handleConfirmDeleteModal}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {t("notification.areYouSureNotificationDelete")}
                    </Typography>

                </div>
            </Modal>
            <Modal
                type="message"
                open={openSuccessDeleteModal}
                confirmText={t("common.close")}
                onClose={handleConfirmSuccessDeleteModal}
                onConfirm={handleConfirmSuccessDeleteModal}
            >
                <div className={styles.modalMessageContentAlignCenter}>
                    <Typography component="h1" variant="largeBold">
                        {t("notification.notificationSuccessfullyDeleted")}
                    </Typography>
                </div>
            </Modal>
        </>
    );
};